/**
 * Google Workspace Integration Service
 * Handles Gmail account provisioning and termination
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('WorkspaceService', 'service');

export type WorkspaceErrorCode = 'LICENSE_LIMIT' | 'EMAIL_EXISTS' | 'PERMISSION_DENIED' | 'CONFIG_ERROR' | 'UNKNOWN';

export interface CreateGmailAccountResult {
  success: boolean;
  email?: string;
  tempPassword?: string;
  error?: string;
  errorCode?: WorkspaceErrorCode;
}

export interface SuspendGmailAccountResult {
  success: boolean;
  suspended?: boolean;
  forwardingEnabled?: boolean;
  error?: string;
}

export interface CreateUserFromRecruitResult {
  success: boolean;
  userId?: string;
  email?: string;
  roles?: string[];
  error?: string;
  errorCode?: WorkspaceErrorCode;
}

// Available roles for user creation
export const AVAILABLE_ROLES = [
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'processor', label: 'Processor' },
  { value: 'admin', label: 'Admin' },
  { value: 'partner', label: 'Partner' },
  { value: 'manager', label: 'Manager' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'underwriter', label: 'Underwriter' },
  { value: 'compliance_specialist', label: 'Compliance Specialist' }
] as const;

/**
 * Create a Gmail account for a recruit
 * Tries firstname@lendwisemtg.com first, then firstnamelastname@lendwisemtg.com
 */
export async function createGmailAccount(
  recruitId: string,
  firstName: string,
  lastName: string
): Promise<CreateGmailAccountResult> {
  try {
    log.info('Creating Gmail account', { recruitId, firstName, lastName });

    const { data, error } = await supabase.functions.invoke('workspace-create-user', {
      body: {
        recruitId,
        firstName,
        lastName
      }
    });

    if (error) {
      log.error('Failed to create Gmail account', { error: error.message });
      return {
        success: false,
        error: error.message || 'Failed to create Gmail account'
      };
    }

    if (!data?.success) {
      log.error('Gmail account creation failed', { error: data?.error, errorCode: data?.errorCode });
      return {
        success: false,
        error: data?.error || 'Failed to create Gmail account',
        errorCode: data?.errorCode as WorkspaceErrorCode
      };
    }

    log.info('Gmail account created successfully', { email: data.email });

    return {
      success: true,
      email: data.email,
      tempPassword: data.tempPassword
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception creating Gmail account', { error: errorMessage });
    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Suspend a Gmail account and set up email forwarding
 * Used when terminating a recruit
 */
export async function suspendGmailAccount(
  recruitId: string,
  email: string,
  forwardTo: string
): Promise<SuspendGmailAccountResult> {
  try {
    log.info('Suspending Gmail account', { recruitId, email, forwardTo });

    const { data, error } = await supabase.functions.invoke('workspace-suspend-user', {
      body: {
        recruitId,
        email,
        forwardTo
      }
    });

    if (error) {
      log.error('Failed to suspend Gmail account', { error: error.message });
      return {
        success: false,
        error: error.message || 'Failed to suspend Gmail account'
      };
    }

    if (!data?.success) {
      log.error('Gmail account suspension failed', { error: data?.error });
      return {
        success: false,
        error: data?.error || 'Failed to suspend Gmail account'
      };
    }

    log.info('Gmail account suspended successfully', {
      email,
      forwardTo,
      suspended: data.suspended,
      forwardingEnabled: data.forwardingEnabled
    });

    return {
      success: true,
      suspended: data.suspended,
      forwardingEnabled: data.forwardingEnabled
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception suspending Gmail account', { error: errorMessage });
    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Get list of active users with @lendwisemtg.com emails
 * Used for the forwarding recipient dropdown
 */
export async function getActiveUsersForForwarding(): Promise<Array<{
  id: string;
  email: string;
  fullName: string;
}>> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, first_name, last_name')
      .eq('employment_status', 'active')
      .ilike('email', '%@lendwisemtg.com')
      .order('first_name');

    if (error) {
      log.error('Failed to fetch active users', { error: error.message });
      return [];
    }

    return (data || []).map(user => ({
      id: user.id,
      email: user.email,
      fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    }));
  } catch (error) {
    log.error('Exception fetching active users', { error });
    return [];
  }
}

export interface LinkRecruitToUserResult {
  success: boolean;
  alreadyLinked?: boolean;
  userEmail?: string;
  encompassUserId?: string | null;
  error?: string;
}

export interface LinkableUser {
  id: string;
  email: string;
  fullName: string;
}

/**
 * List portal users eligible for "Link Existing Gmail" via the authorized
 * edge function. Client-side users queries are RLS-scoped to own/admin/partner
 * rows, so non-admin Talent Pipeline users need this server-side path.
 */
export async function getLinkableUsers(recruitId: string): Promise<{ users: LinkableUser[]; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('link-recruit-to-user', {
      body: { action: 'candidates', recruitId }
    });

    if (error) {
      log.error('Failed to load linkable users', { metadata: { error: error.message } });
      return { users: [], error: error.message || 'Failed to load portal users' };
    }
    if (!data?.success) {
      log.error('Linkable user listing failed', { metadata: { error: data?.error } });
      return { users: [], error: data?.error || 'Failed to load portal users' };
    }

    return { users: Array.isArray(data.candidates) ? data.candidates : [] };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception loading linkable users', { metadata: { error: errorMessage } });
    return { users: [], error: errorMessage };
  }
}

export interface RecruitLinkedState {
  linked: boolean;
  userId: string | null;
  userEmail: string | null;
  encompassUserId: string | null;
  error?: string;
}

/**
 * Read a recruit's linked-user state (linked user id/email + Encompass ID)
 * through the authorized edge function. The drawer cannot query users
 * directly: RLS hides other users' rows from non-admin Talent Pipeline users.
 */
export async function getRecruitLinkedState(recruitId: string): Promise<RecruitLinkedState> {
  try {
    const { data, error } = await supabase.functions.invoke('link-recruit-to-user', {
      body: { action: 'linked-state', recruitId }
    });

    if (error || !data?.success) {
      const message = error?.message || data?.error || 'Failed to load linked user state';
      log.error('Failed to load recruit linked state', { metadata: { error: message } });
      return { linked: false, userId: null, userEmail: null, encompassUserId: null, error: message };
    }

    return {
      linked: data.linked === true,
      userId: typeof data.userId === 'string' ? data.userId : null,
      userEmail: typeof data.userEmail === 'string' ? data.userEmail : null,
      encompassUserId: typeof data.encompassUserId === 'string' ? data.encompassUserId : null
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception loading recruit linked state', { metadata: { error: errorMessage } });
    return { linked: false, userId: null, userEmail: null, encompassUserId: null, error: errorMessage };
  }
}

/**
 * Link a recruit to an existing portal user (existing-access onboarding).
 * The edge function atomically sets the recruit's company email to the user's
 * email, marks onboarding_access_mode='existing', and clears any temp password.
 */
export async function linkRecruitToUser(
  recruitId: string,
  userId: string
): Promise<LinkRecruitToUserResult> {
  try {
    log.info('Linking recruit to existing user', { metadata: { recruitId, userId } });

    const { data, error } = await supabase.functions.invoke('link-recruit-to-user', {
      body: { recruitId, userId }
    });

    if (error) {
      log.error('Failed to link recruit to user', { metadata: { error: error.message } });
      return { success: false, error: error.message || 'Failed to link recruit to user' };
    }

    if (!data?.success) {
      log.error('Recruit link failed', { metadata: { error: data?.error } });
      return { success: false, error: data?.error || 'Failed to link recruit to user' };
    }

    log.info('Recruit linked to existing user', {
      metadata: {
        recruitId,
        userId,
        alreadyLinked: data.alreadyLinked === true
      }
    });

    return {
      success: true,
      alreadyLinked: data.alreadyLinked === true,
      userEmail: data.userDetails?.email,
      encompassUserId: typeof data.encompassUserId === 'string' ? data.encompassUserId : null
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception linking recruit to user', { metadata: { error: errorMessage } });
    return { success: false, error: errorMessage };
  }
}

/**
 * Create a user account from a recruit
 * Creates auth user, public user record, and links to recruit
 */
export async function createUserFromRecruit(
  recruitId: string,
  roles: string[]
): Promise<CreateUserFromRecruitResult> {
  try {
    log.info('Creating user from recruit', { recruitId, roles });

    const { data, error } = await supabase.functions.invoke('create-user-from-recruit', {
      body: {
        recruitId,
        roles
      }
    });

    if (error) {
      log.error('Failed to create user from recruit', { error: error.message });
      return {
        success: false,
        error: error.message || 'Failed to create user account'
      };
    }

    if (!data?.success) {
      log.error('User creation from recruit failed', { error: data?.error, errorCode: data?.errorCode });
      return {
        success: false,
        error: data?.error || 'Failed to create user account',
        errorCode: data?.errorCode as WorkspaceErrorCode
      };
    }

    log.info('User created from recruit successfully', {
      userId: data.userId,
      email: data.email,
      roles: data.roles
    });

    return {
      success: true,
      userId: data.userId,
      email: data.email,
      roles: data.roles
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    log.error('Exception creating user from recruit', { error: errorMessage });
    return {
      success: false,
      error: errorMessage
    };
  }
}
